import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PermMatrix, StorePicker } from "@/components/admin/ProfilesTab";
import { api, errMsg } from "@/lib/api";

const MODES = [
  ["purchases", "Acquisti nei PdV", "Vede clienti e movimenti effettuati nei suoi punti vendita; lo storico fatto altrove resta nascosto"],
  ["issued", "Carte emesse dai PdV", "Vede solo i clienti con carta emessa dai suoi punti vendita, con tutto il loro storico"],
  ["both", "Entrambi", "Clienti che hanno acquistato nei suoi PdV oppure con carta emessa lì"],
];
const CHANNELS = [["email", "Email"], ["sms", "SMS"], ["whatsapp", "WhatsApp"]];

export const AccessDialog = ({ user, profiles, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [sections, setSections] = useState([]);
  const [stores, setStores] = useState([]);
  useEffect(() => { if (user) setF({ ...user.access }); }, [user]);
  useEffect(() => { api.get("/admin/sections").then((r) => setSections(r.data)).catch(() => {}); api.get("/stores").then((r) => setStores(r.data)).catch(() => {}); }, []);
  if (!user || !f) return null;
  const prof = profiles.find((p) => p.id === user.profile_id);
  const startCustom = (v) => setF({ ...f, custom: v, ...(v && !Object.keys(f.permissions || {}).length && prof ? { permissions: prof.permissions, stores: prof.stores } : {}) });
  const save = async () => {
    try { const { data } = await api.put(`/admin/users/${user.id}/access`, { ...f, monthly_cap: Number(f.monthly_cap) || 0 }); toast.success("Accesso aggiornato"); onSaved(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto bg-white" data-testid="access-dialog">
        <DialogHeader><DialogTitle>Personalizza accesso · {user.name || user.email}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <label className="flex items-center gap-3 text-sm text-slate-800"><Switch checked={f.custom} onCheckedChange={startCustom} data-testid="access-custom-switch" />
              <span><b>Personalizza sezioni e punti vendita</b><span className="block text-xs text-slate-500">Disattivo: valgono quelli del profilo "{prof?.name || "—"}". Attivo: le impostazioni qui sotto sostituiscono il profilo per questo utente.</span></span></label>
          </div>
          {f.custom && (
            <div className="grid gap-5">
              <div><div className="mb-1.5 text-xs font-semibold text-slate-700">Sezioni abilitate</div><PermMatrix sections={sections} value={f.permissions || {}} onChange={(p) => setF({ ...f, permissions: p })} /></div>
              <div><div className="mb-1.5 text-xs font-semibold text-slate-700">Punti vendita (anche più di uno, es. gruppo)</div><StorePicker stores={stores} value={f.stores || []} onChange={(s) => setF({ ...f, stores: s })} /></div>
            </div>
          )}
          <div>
            <div className="mb-1.5 text-xs font-semibold text-slate-700">Regola di visibilità clienti (quando i punti vendita sono limitati)</div>
            <div className="grid gap-2 sm:grid-cols-3">
              {MODES.map(([k, l, d]) => (
                <button key={k} type="button" data-testid={`access-mode-${k}`} onClick={() => setF({ ...f, store_mode: k })}
                  className={`rounded-lg border p-3 text-left transition-colors ${f.store_mode === k ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <div className="text-sm font-medium text-slate-800">{l}</div><div className="mt-0.5 text-[11px] leading-snug text-slate-500">{d}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
            <div>
              <div className="mb-1.5 text-xs font-semibold text-slate-700">Canali messaggi abilitati (scheda cliente e campagne)</div>
              <div className="flex flex-wrap gap-4">
                {CHANNELS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={f.channels?.[k] !== false} onCheckedChange={(v) => setF({ ...f, channels: { ...f.channels, [k]: v } })} data-testid={`access-channel-${k}`} />{l}</label>)}
              </div>
            </div>
            <label className="text-xs font-semibold text-slate-700">Tetto mensile messaggi<Input className="mt-1.5" type="number" min="0" data-testid="access-cap-input" value={f.monthly_cap ?? 0} onChange={(e) => setF({ ...f, monthly_cap: e.target.value })} /><span className="mt-1 block text-[11px] font-normal text-slate-500">0 = nessun limite. Al raggiungimento l'invio viene bloccato.</span></label>
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button><Button data-testid="access-save-button" className="bg-blue-600 hover:bg-blue-700" onClick={save}>Salva accesso</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
